import { useEffect, useRef, useState } from "react";

// Calm login rotation: one real image + one short line per slide.
// Full-bleed photography, ~5.5s per slide, 1.4s crossfade, slow scale settle. Pauses on hover and when the tab is hidden.
// prefers-reduced-motion shows the first slide only. Images load one ahead.
const SLIDE_MS = 5500;
const FADE_MS = 1400;

export const BRICKS_SLIDES = [
  { src: "/media/login/listing", pos: "50% 40%", alt: "Brick facade of a renovated home, listing photo by Bricks", line: "Listing media, booked to the minute." },
  { src: "/media/login/visit", pos: "38% 50%", alt: "Living room interior captured for a Virtual Visit", line: "Virtual Visit. Walk the home from anywhere." },
  { src: "/media/login/site", pos: "55% 50%", alt: "Drone view over an active construction site", line: "Site Progress. Maps, models and reports." },
  { src: "/media/login/drone", pos: "45% 50%", alt: "Drone view of an industrial waterfront site", line: "Aerials for commercial and industrial sites." },
];
const WIDTHS = [1000, 1600];

function useReducedMotion() {
  const [r, setR] = useState(() => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    const m = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!m) return undefined;
    const on = () => setR(m.matches);
    m.addEventListener?.("change", on);
    return () => m.removeEventListener?.("change", on);
  }, []);
  return r;
}

export default function LoginSlides({ slides = BRICKS_SLIDES }) {
  const reduced = useReducedMotion();
  const [i, setI] = useState(0);
  const [loaded, setLoaded] = useState(() => new Set([0, 1 % slides.length]));
  const hover = useRef(false);
  const [hidden, setHidden] = useState(typeof document !== "undefined" && document.hidden);

  useEffect(() => {
    const on = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", on);
    return () => document.removeEventListener("visibilitychange", on);
  }, []);

  useEffect(() => {
    if (reduced || hidden || slides.length < 2) return undefined;
    const t = setInterval(() => { if (!hover.current) setI((x) => (x + 1) % slides.length); }, SLIDE_MS);
    return () => clearInterval(t);
  }, [reduced, hidden, slides.length]);

  useEffect(() => {
    const next = (i + 1) % slides.length;
    setLoaded((s) => (s.has(next) ? s : new Set([...s, next])));
  }, [i, slides.length]);

  const cur = reduced ? 0 : i;
  return (
    <div
      className="login-slides"
      onMouseEnter={() => { hover.current = true; }}
      onMouseLeave={() => { hover.current = false; }}
      style={{ position: "absolute", inset: 0, background: "#1a1612", overflow: "hidden" }}
      aria-roledescription="carousel"
      aria-label="What Bricks does"
    >
      {slides.map((s, k) => (
        <div key={s.src} aria-hidden={k !== cur} style={{ position: "absolute", inset: 0, opacity: k === cur ? 1 : 0, transition: reduced ? "none" : `opacity ${FADE_MS}ms ease` }}>
          {loaded.has(k) && (
            <img
              className="ls-img"
              src={`${s.src}-${WIDTHS[0]}.webp`}
              srcSet={WIDTHS.map((w) => `${s.src}-${w}.webp ${w}w`).join(", ")}
              sizes="(min-width: 1810px) 760px, 42vw"
              alt={s.alt}
              decoding="async"
              loading={k === 0 ? "eager" : "lazy"}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: s.pos || "50% 50%", transform: !reduced && k === cur ? "scale(1)" : "scale(1.045)", transition: reduced ? "none" : `transform ${SLIDE_MS + FADE_MS * 2}ms cubic-bezier(.2,.6,.2,1)` }}
            />
          )}
        </div>
      ))}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "linear-gradient(to top, rgba(12,10,8,.58) 0%, rgba(12,10,8,.22) 22%, rgba(12,10,8,0) 42%)" }} />
      <div style={{ position: "absolute", left: "clamp(32px, 4vw, 56px)", right: "clamp(32px, 4vw, 56px)", bottom: "clamp(32px, 4.5vw, 56px)" }}>
        <style>{"@keyframes lsIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}@media (prefers-reduced-motion: reduce){.ls-line{animation:none!important}}"}</style>
        <div key={cur} className="ls-line" aria-live="polite" style={{ animation: "lsIn 900ms ease both", fontSize: "clamp(22px, 1.75vw, 30px)", fontWeight: 400, letterSpacing: "-0.02em", lineHeight: 1.2, color: "#F4F1EB", maxWidth: 460, minHeight: 36, textShadow: "0 1px 18px rgba(0,0,0,.25)" }}>
          {slides[cur].line}
        </div>
        {!reduced && slides.length > 1 && (
          <div style={{ display: "flex", gap: 6, marginTop: 20 }} aria-hidden="true">
            {slides.map((s, k) => (
              <span key={s.src} style={{ width: 6, height: 6, borderRadius: 0, background: k === cur ? "#F4F1EB" : "rgba(244,241,235,.3)", transition: "background .6s ease" }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
